import { useState } from "react";
import { Hand } from "lucide-react";

import { cn } from "@/lib/utils";
import { MANUAL_ARM_PHRASE, ManualActionError, type ManualCockpitState } from "../../hooks/useManualCockpit";

/**
 * LA maison de l'armement du cockpit MANUEL (dual-cockpit 2026-07-30 ;
 * range-only depuis 2026-09-01 — le ticket d'ordre libre est retiré, la seule
 * ouverture est l'automate range). Interrupteur armer/couper (phrase
 * « ARM MANUEL » + OTP e-mail, TTL 12 h) : la fenêtre armée est la licence du
 * range. Identité visuelle bleue (--color-info) pour ne jamais se confondre
 * avec le cyan AUTO.
 */
export default function ManualTradeCard({ manual }: { manual: ManualCockpitState }) {
  const [arming, setArming] = useState(false);
  const [phrase, setPhrase] = useState("");
  const [otp, setOtp] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const enabled = manual.status?.enabled ?? false;
  const armed = manual.status?.armed ?? false;

  const beginArm = async () => {
    setErr(null);
    setArming(true);
    setOtpSent(false);
    try {
      await manual.requestOtp();
      setOtpSent(true);
    } catch {
      setErr("Impossible d'envoyer le code par e-mail. Réessaie.");
    }
  };

  const confirmArm = async () => {
    setErr(null);
    try {
      await manual.arm(phrase.trim(), otp.trim());
      resetArmForm();
    } catch (e) {
      setErr(e instanceof ManualActionError ? e.detail : "Impossible d'armer le cockpit MANUEL.");
    }
  };

  const resetArmForm = () => {
    setArming(false);
    setPhrase("");
    setOtp("");
    setOtpSent(false);
    setErr(null);
  };

  const toggle = () => {
    if (armed) {
      void manual.disarm();
    } else if (!arming) {
      void beginArm();
    }
  };

  const tone = !enabled ? "disabled" : armed ? "armed" : "off";

  return (
    <div
      className={cn(
        "flex flex-col gap-3 rounded-xl border p-4 transition-colors",
        tone === "armed" && "border-info/45 bg-info-soft",
        tone === "off" && "border-border bg-background/30",
        tone === "disabled" && "border-border bg-background/20 opacity-70",
      )}
      data-testid="manual-card"
      data-tone={tone}
    >
      {/* en-tête : titre + interrupteur */}
      <div className="flex items-center gap-3">
        <div
          className={cn(
            "grid size-9 shrink-0 place-items-center rounded-lg border",
            armed ? "border-info/40 bg-info/10 text-info" : "border-border text-muted-foreground",
          )}
        >
          <Hand className="size-5" aria-hidden />
        </div>
        <div className="min-w-0">
          <div className="text-foreground flex items-center gap-2 font-semibold">
            Cockpit MANUEL
            {armed && (
              <span className="text-info inline-flex items-center gap-1 text-xs font-medium">
                <span className="bg-info size-1.5 animate-pulse rounded-full" /> armé
              </span>
            )}
          </div>
          <div className="text-muted-foreground text-xs">
            {!enabled
              ? "Désactivé — MANUAL_ACCOUNT_ID absent du déploiement."
              : armed
                ? `Compte ${manual.status?.accountId} — tes décisions, exécution aux rails §19.`
                : "Trading manuel sur compte dédié — armer pour agir."}
          </div>
        </div>
        <div className="ml-auto">
          <Switch on={armed} onClick={toggle} disabled={manual.pending || !enabled} />
        </div>
      </div>

      {/* flux d'armement */}
      {arming && (
        <div className="border-info/30 flex flex-col gap-2.5 rounded-lg border bg-card p-3">
          <p className="text-muted-foreground text-sm">
            Armement du cockpit manuel. Tape <strong className="text-info">{MANUAL_ARM_PHRASE}</strong> et
            le code reçu par e-mail.
          </p>
          <input
            type="text"
            value={phrase}
            onChange={(e) => setPhrase(e.target.value)}
            placeholder={MANUAL_ARM_PHRASE}
            aria-label="Phrase de confirmation"
            className="bg-background border-border focus:border-info rounded-lg border px-3 py-2 text-sm outline-none"
          />
          <input
            type="text"
            inputMode="numeric"
            value={otp}
            onChange={(e) => setOtp(e.target.value)}
            placeholder={otpSent ? "code à 6 chiffres reçu par e-mail" : "demande du code…"}
            aria-label="Code à usage unique"
            className="bg-background border-border focus:border-info rounded-lg border px-3 py-2 font-mono text-sm outline-none"
          />
          {err && (
            <p className="text-loss text-sm" role="alert">
              {err}
            </p>
          )}
          <div className="flex gap-2">
            <button
              type="button"
              onClick={confirmArm}
              disabled={manual.pending || phrase.trim() === "" || otp.trim() === ""}
              className="bg-info flex-1 rounded-lg px-4 py-2 text-sm font-bold text-black disabled:opacity-50"
              data-testid="manual-confirm-arm"
            >
              {manual.pending ? "Armement…" : "Armer MANUEL"}
            </button>
            <button
              type="button"
              onClick={resetArmForm}
              className="border-border text-muted-foreground hover:text-foreground rounded-lg border px-4 py-2 text-sm"
            >
              Annuler
            </button>
          </div>
        </div>
      )}

    </div>
  );
}

/** Interrupteur piste+pouce (role=switch) — identité bleue MANUEL. */
function Switch({ on, onClick, disabled }: { on: boolean; onClick: () => void; disabled?: boolean }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      aria-label="Cockpit MANUEL"
      onClick={onClick}
      disabled={disabled}
      data-testid="manual-switch"
      className={cn(
        "focus-visible:outline-info relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 disabled:opacity-50",
        on ? "bg-info" : "bg-muted",
      )}
    >
      <span
        className={cn(
          "inline-block size-5 transform rounded-full bg-white shadow transition-transform",
          on ? "translate-x-[1.375rem]" : "translate-x-0.5",
        )}
      />
    </button>
  );
}
